import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Customer, CustomerProfileUpdate } from '../types'
import { customerAuthApi } from '../api/client'

interface AuthState {
  customer: Customer | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean

  /**
   * Step 1: request an OTP to be sent to the phone number.
   * Pass `requireExisting` (login flow) to reject unknown phone numbers.
   * Includes country_code (ISO) and country (name) for international support.
   * Optional firstName, lastName, email are used to update customer before OTP.
   * Returns { is_new_customer, delivery_channel } to indicate if customer is new and how OTP was sent.
   */
  requestOTP: (phone: string, countryCode: string, country: string, requireExisting?: boolean, firstName?: string, lastName?: string, email?: string) => Promise<{ is_new_customer: boolean; delivery_channel: string }>
  /** Step 2: verify the OTP and log in — stores tokens and loads profile. Includes country information. */
  verifyOTP: (phone: string, code: string, countryCode: string, country: string) => Promise<void>
  /** Steps 3 & 4: persist the post-verification details wizard. */
  updateProfile: (data: CustomerProfileUpdate) => Promise<void>
  logout: () => Promise<void>
  /**
   * Resolve the persisted session. On public pages pass `{ skipAuthRedirect: true }`
   * so a dead session simply resolves as "not logged in" (no /login redirect).
   */
  loadCustomer: (opts?: { skipAuthRedirect?: boolean }) => Promise<void>
  setTokens: (access: string, refresh: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      customer: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      setTokens: (access, refresh) => {
        localStorage.setItem('access_token', access)
        localStorage.setItem('refresh_token', refresh)
        set({ accessToken: access, refreshToken: refresh, isAuthenticated: true })
      },

      requestOTP: async (phone, countryCode, country, requireExisting = false, firstName = '', lastName = '', email = '') => {
        set({ isLoading: true })
        try {
          const res = await customerAuthApi.requestOTP(phone, countryCode, country, requireExisting, firstName, lastName, email)
          // Response is wrapped in ApiResponse envelope, so access nested data.data
          const data = (res.data as any).data
          return { is_new_customer: data.is_new_customer, delivery_channel: data.delivery_channel || 'whatsapp' }
        } finally {
          set({ isLoading: false })
        }
      },

      verifyOTP: async (phone, code, countryCode, country) => {
        set({ isLoading: true })
        try {
          const tokens = await customerAuthApi.verifyOTP(phone, code, countryCode, country)
          get().setTokens(tokens.access_token, tokens.refresh_token)
          const customer = await customerAuthApi.me()
          set({ customer, isLoading: false })
        } catch (err) {
          set({ isLoading: false })
          throw err
        }
      },

      updateProfile: async (data) => {
        set({ isLoading: true })
        try {
          const customer = await customerAuthApi.updateProfile(data)
          set({ customer, isLoading: false })
        } catch (err) {
          set({ isLoading: false })
          throw err
        }
      },

      logout: async () => {
        const rt = get().refreshToken
        if (rt) {
          try { await customerAuthApi.logout(rt) } catch { /* ignore */ }
        }
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        set({ customer: null, accessToken: null, refreshToken: null, isAuthenticated: false })
      },

      loadCustomer: async (opts) => {
        const token = localStorage.getItem('access_token')
        if (!token) {
          // No token at all — ensure the store reflects a logged-out state so
          // gates can render the register modal instead of a stale session.
          set({ isAuthenticated: false, customer: null })
          return
        }
        try {
          const customer = await customerAuthApi.me({ skipAuthRedirect: opts?.skipAuthRedirect })
          set({ customer, isAuthenticated: true, accessToken: token })
        } catch {
          localStorage.removeItem('access_token')
          set({ isAuthenticated: false, customer: null })
        }
      },
    }),
    {
      name: 'localgrader-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
